import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { supabase } from '../../lib/supabase';
import { manageContents, manageTags } from '../../lib/api';
import {
  FileText,
  Plus,
  Edit, 
  Trash2, 
  Save, 
  X, 
  Search, 
  Filter,
  Eye,
  Upload,
  Link as LinkIcon,
  Calendar,
  Tag as TagIcon
} from 'lucide-react';
import toast from 'react-hot-toast';

interface Content {
  id: string;
  name: string;
  source_url: string | null;
  content_type: 'file' | 'text';
  content_data: string | null;
  source_tags: string[];
  topic_tags: string[];
  training_type_tags: string[];
  training_subtype_tags: string[];
  status: 'draft' | 'published';
  created_at: string;
}

interface Tag {
  id: string;
  name: string;
  type: 'source' | 'topic' | 'thinking_type';
}

interface ContentFormData {
  name: string;
  source_url: string;
  content_type: 'file' | 'text';
  content_data: string;
  source_tags: string[];
  topic_tags: string[];
  training_type_tags: string[];
  training_subtype_tags: string[];
}

const ContentManagement: React.FC = () => {
  const [contents, setContents] = useState<Content[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState<Content | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedContentType, setSelectedContentType] = useState<string>('');
  const [viewingContent, setViewingContent] = useState<Content | null>(null);
  const [formData, setFormData] = useState<ContentFormData>({
    name: '',
    source_url: '',
    content_type: 'text',
    content_data: '',
    source_tags: [],
    topic_tags: [],
    training_type_tags: [],
    training_subtype_tags: [],
  });

  useEffect(() => {
    fetchContents();
    fetchTags();
  }, []);

  const fetchContents = async () => {
    try {
      const response = await manageContents('list');
      
      if (response.success && response.data) {
        // The API returns data in response.data.contents, not response.data directly
        const contentsData = (response.data as any).contents || [];
        setContents(contentsData);
      } else {
        throw new Error(response.error || '获取内容失败');
      }
    } catch (error) {
      console.error('获取内容失败:', error);
      toast.error('获取内容失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await manageTags('list');
      
      if (response.success && response.data) {
        // The API returns data in response.data.tags, not response.data directly
        const tagsData = (response.data as any).tags || [];
        setTags(tagsData);
      } else {
        throw new Error(response.error || '获取标签失败');
      }
    } catch (error) {
      console.error('获取标签失败:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('请输入内容名称');
      return;
    }

    if (formData.content_type === 'text' && !formData.content_data.trim()) {
      toast.error('请输入文本内容');
      return;
    }

    try {
      const contentData = {
        name: formData.name.trim(),
        source_url: formData.source_url.trim() || null,
        content_type: formData.content_type,
        content_data: formData.content_data.trim() || null,
        source_tags: formData.source_tags,
        topic_tags: formData.topic_tags,
        training_type_tags: formData.training_type_tags,
        training_subtype_tags: formData.training_subtype_tags,
      };

      if (editingContent) {
        // 更新内容
        const response = await manageContents('update', {
          id: editingContent.id,
          ...contentData
        });

        if (!response.success) {
          throw new Error(response.error || '更新内容失败');
        }
        toast.success('内容更新成功！');
      } else {
        // 创建新内容
        const response = await manageContents('create', contentData);

        if (!response.success) {
          throw new Error(response.error || '创建内容失败');
        }
        toast.success('内容创建成功！');
      }

      resetForm();
      fetchContents();
    } catch (error: any) {
      console.error('保存内容失败:', error);
      toast.error('保存内容失败');
    }
  };

  const handleEdit = (content: Content) => {
    setEditingContent(content);
    setFormData({
      name: content.name,
      source_url: content.source_url || '',
      content_type: content.content_type,
      content_data: content.content_data || '',
      source_tags: content.source_tags,
      topic_tags: content.topic_tags,
      training_type_tags: content.training_type_tags,
      training_subtype_tags: content.training_subtype_tags,
    });
    setShowForm(true);
  };

  const handleDelete = async (contentId: string) => {
    if (!confirm('确定要删除这个内容吗？')) return;

    try {
      const response = await manageContents('delete', { id: contentId });
      
      if (!response.success) {
        throw new Error(response.error || '删除内容失败');
      }
      
      toast.success('内容删除成功！');
      fetchContents();
    } catch (error) {
      console.error('删除内容失败:', error);
      toast.error('删除内容失败');
    }
  };

  const handlePublish = async (contentId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    
    try {
      const response = await manageContents('update', {
        id: contentId,
        status: newStatus
      });

      if (!response.success) {
        throw new Error(response.error || '更新状态失败');
      }
      
      toast.success(`内容${newStatus === 'published' ? '发布' : '取消发布'}成功！`);
      fetchContents();
    } catch (error) {
      console.error('更新状态失败:', error);
      toast.error('更新状态失败');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      source_url: '',
      content_type: 'text',
      content_data: '',
      source_tags: [],
      topic_tags: [],
      training_type_tags: [],
      training_subtype_tags: [],
    });
    setEditingContent(null);
    setShowForm(false);
  };

  const handleTagChange = (tagType: keyof Pick<ContentFormData, 'source_tags' | 'topic_tags' | 'training_type_tags' | 'training_subtype_tags'>, tagName: string) => {
    const currentTags = formData[tagType];
    const newTags = currentTags.includes(tagName)
      ? currentTags.filter(tag => tag !== tagName)
      : [...currentTags, tagName];
    
    setFormData({ ...formData, [tagType]: newTags });
  };

  const getTagsByType = (type: string) => {
    return tags.filter(tag => tag.type === type);
  };

  const filteredContents = contents.filter(content => {
    const matchesSearch = content.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === '' || content.status === selectedStatus;
    const matchesType = selectedContentType === '' || content.content_type === selectedContentType;
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
        {/* Header Actions */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="搜索内容..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">所有状态</option>
                <option value="draft">草稿</option>
                <option value="published">已发布</option>
              </select>
              <select
                value={selectedContentType}
                onChange={(e) => setSelectedContentType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">所有类型</option>
                <option value="text">文本</option>
                <option value="file">文件</option>
              </select>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            新建内容
          </button>
        </div>

        {/* Content Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {editingContent ? '编辑内容' : '新建内容'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 基本信息 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        内容名称 *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="请输入内容名称"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        来源地址
                      </label>
                      <input
                        type="url"
                        value={formData.source_url}
                        onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>

                  {/* 内容类型 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      内容类型 *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="text"
                          checked={formData.content_type === 'text'}
                          onChange={(e) => setFormData({ ...formData, content_type: e.target.value as 'text' | 'file' })}
                          className="mr-2"
                        />
                        文本内容
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="file"
                          checked={formData.content_type === 'file'}
                          onChange={(e) => setFormData({ ...formData, content_type: e.target.value as 'text' | 'file' })}
                          className="mr-2"
                        />
                        文件上传
                      </label>
                    </div>
                  </div>

                  {/* 内容数据 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.content_type === 'text' ? '文本内容 *' : '文件信息'}
                    </label>
                    {formData.content_type === 'text' ? (
                      <textarea
                        value={formData.content_data}
                        onChange={(e) => setFormData({ ...formData, content_data: e.target.value })}
                        className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="请输入文本内容..."
                        required
                      />
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2">文件上传功能</p>
                        <p className="text-sm text-gray-500">暂时请手动输入文件ID或路径</p>
                        <input
                          type="text"
                          value={formData.content_data}
                          onChange={(e) => setFormData({ ...formData, content_data: e.target.value })}
                          className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="文件ID或路径"
                        />
                      </div>
                    )}
                  </div>

                  {/* 标签选择 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 来源标签 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        来源标签
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                        {getTagsByType('source').map(tag => (
                          <label key={tag.id} className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              checked={formData.source_tags.includes(tag.name)}
                              onChange={() => handleTagChange('source_tags', tag.name)}
                              className="mr-2"
                            />
                            <span className="text-sm">{tag.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* 主题标签 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        主题标签
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                        {getTagsByType('topic').map(tag => (
                          <label key={tag.id} className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              checked={formData.topic_tags.includes(tag.name)}
                              onChange={() => handleTagChange('topic_tags', tag.name)}
                              className="mr-2"
                            />
                            <span className="text-sm">{tag.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* 思维类型标签 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        思维类型标签
                      </label>
                      <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                        {getTagsByType('thinking_type').map(tag => (
                          <label key={tag.id} className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              checked={formData.training_type_tags.includes(tag.name)}
                              onChange={() => handleTagChange('training_type_tags', tag.name)}
                              className="mr-2"
                            />
                            <span className="text-sm">{tag.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* 训练子类型标签 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        训练子类型标签
                      </label>
                      <input
                        type="text"
                        value={formData.training_subtype_tags.join(', ')}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          training_subtype_tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="用逗号分隔多个标签"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editingContent ? '更新' : '创建'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Contents List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              内容列表 ({filteredContents.length})
            </h2>
          </div>

          {filteredContents.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredContents.map((content) => (
                <div key={content.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 mb-2">
                        <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {content.name}
                          </h3>
                          {content.source_url && (
                            <div className="flex items-center mt-1 text-sm text-gray-500">
                              <LinkIcon className="w-4 h-4 mr-1" />
                              <a 
                                href={content.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="hover:text-blue-600 truncate"
                              >
                                {content.source_url}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* 标签显示 */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          content.content_type === 'text' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {content.content_type === 'text' ? '文本' : '文件'}
                        </span>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          content.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {content.status === 'published' ? '已发布' : '草稿'}
                        </span>
                        {content.source_tags.map((tag, index) => (
                          <span key={index} className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                            来源: {tag}
                          </span>
                        ))}
                        {content.topic_tags.map((tag, index) => (
                          <span key={index} className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            主题: {tag}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(content.created_at).toLocaleString()}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePublish(content.id, content.status)}
                        className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                          content.status === 'published'
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {content.status === 'published' ? '取消发布' : '发布'}
                      </button>
                      <button
                        onClick={() => setViewingContent(content)}
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                        title="查看详情"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(content)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(content.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm || selectedStatus || selectedContentType ? '没有找到匹配的内容' : '暂无内容'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedStatus || selectedContentType ? '尝试调整搜索条件' : '开始创建第一个内容'}
              </p>
              {!searchTerm && !selectedStatus && !selectedContentType && (
                <button
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  创建内容
                </button>
              )}
            </div>
          )}
        </div>

        {/* Content View Modal */}
        {viewingContent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    内容详情
                  </h2>
                  <button
                    onClick={() => setViewingContent(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* 内容名称 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">内容名称</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-800">{viewingContent.name}</p>
                    </div>
                  </div>

                  {/* 来源地址 */}
                  {viewingContent.source_url && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">来源地址</h3>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <a 
                          href={viewingContent.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 break-all"
                        >
                          {viewingContent.source_url}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* 内容数据 */}
                  {viewingContent.content_data && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">内容数据</h3>
                      <div className="p-4 bg-gray-50 rounded-lg prose max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-3 last:mb-0 text-gray-700">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-700">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-700">{children}</ol>,
                            li: ({ children }) => <li className="text-gray-700">{children}</li>,
                            h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-gray-900">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-gray-900">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-gray-900">{children}</h3>,
                            strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                            em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
                            code: ({ children }) => <code className="bg-gray-200 px-2 py-1 rounded text-sm font-mono text-gray-800">{children}</code>,
                            pre: ({ children }) => <pre className="bg-gray-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                            blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-3 text-gray-600">{children}</blockquote>,
                          }}
                        >
                          {viewingContent.content_data}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* 标签信息 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">标签信息</h3>
                    <div className="space-y-3">
                      {/* 状态 */}
                      <div>
                        <span className="text-sm font-medium text-gray-600 mr-2">状态:</span>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          viewingContent.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {viewingContent.status === 'published' ? '已发布' : '草稿'}
                        </span>
                      </div>

                      {/* 内容类型 */}
                      <div>
                        <span className="text-sm font-medium text-gray-600 mr-2">类型:</span>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                          viewingContent.content_type === 'text' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {viewingContent.content_type === 'text' ? '文本' : '文件'}
                        </span>
                      </div>

                      {/* 来源标签 */}
                      {viewingContent.source_tags.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-600 mr-2">来源标签:</span>
                          <div className="inline-flex flex-wrap gap-1">
                            {viewingContent.source_tags.map((tag, index) => (
                              <span key={index} className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 主题标签 */}
                      {viewingContent.topic_tags.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-600 mr-2">主题标签:</span>
                          <div className="inline-flex flex-wrap gap-1">
                            {viewingContent.topic_tags.map((tag, index) => (
                              <span key={index} className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 思维类型标签 */}
                      {viewingContent.training_type_tags.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-600 mr-2">思维类型:</span>
                          <div className="inline-flex flex-wrap gap-1">
                            {viewingContent.training_type_tags.map((tag, index) => (
                              <span key={index} className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 创建时间 */}
                      <div>
                        <span className="text-sm font-medium text-gray-600 mr-2">创建时间:</span>
                        <span className="text-sm text-gray-700">
                          {new Date(viewingContent.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={() => setViewingContent(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    关闭
                  </button>
                  <button
                    onClick={() => {
                      handleEdit(viewingContent);
                      setViewingContent(null);
                    }}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    编辑内容
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default ContentManagement;